// pages/NewbiePage.jsx
/**
 * NewbiePage (신규 가입자 상세 페이지 컨테이너)
 * --------------------------------------------------------------------
 * 목적:
 *  - 신규 가입자 리스트(NewbieDetail 테이블), 날짜 선택(달력),
 *    언어/기간 드롭다운, 포인트 상세 모달, 하단 내비게이션까지
 *    단일 라우트에서 오케스트레이션.
 *
 * 데이터 흐름:
 *  - useNewbieData 훅: { currentDate, visitDetailValue, isLoadingState, storeId, validDateList } 제공.
 *  - CalendarContent에서 날짜 선택 → fetchNewbie(dateString) 디스패치 → 스토어 갱신.
 *  - NewbieDetail은 visitDetailValue[page]를 테이블로 렌더.
 *  - 행의 "조회" 버튼 → 선택 사용자 phone_number로 PointDetails 모달 열기.
 *
 * 상태/동작:
 *  - page: 'daily' | 'weekly' | 'monthly' (기본 daily)
 *  - leftSide/rightSide: 좌/우 사이드 패널(드롭다운/달력) 개폐. 동시 열림 방지.
 *  - isModalOpen/selectedPhone: 포인트 상세 모달 상태/대상자 전화번호.
 *  - MIN_DATE: 조회 가능한 최소 날짜(2025-07-31 이전 요청 차단).
 *  - pageshow 이벤트: bfcache(뒤로가기 복원) 케이스에서 패널 열린 상태를 강제 초기화.
 *
 */

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactModal from "react-modal";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Header from "../../component/common/Header";
import DropDownMenu from "../../component/common/DropDownMenu";
import CalendarContent from "../../component/common/CalendarContent";
import BottomNavbar from "../../component/common/BottomNavbar";
import LoadingScreen from "../../component/common/LoadingScreen";
import NewbieDetail from "../../component/report/NewbieDetail";
import PointDetails from "../../pages/PointPages/PointDetails";
import useNewbieData from "../../hooks/useNewbieData";
import { fetchNewbie } from "../../store/slices/newbieSlice";

export default function NewbiePage() {
  const location = useLocation(); // 라우팅 state(page, currentDate) 복원에 사용
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  // 기간 탭/타입 및 사이드 패널 상태
  const [page, setPage] = useState("daily");
  const [pageType, setPageType] = useState("");
  const [leftSide, setLeftSide] = useState(false);
  const [rightSide, setRightSide] = useState(false);

  // 포인트 상세 모달
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState("");

  // 드롭다운 전환용 목록
  const pageList = ["daily", "weekly", "monthly"];
  const pageTypeList = ["bestMenu", "bestMenu", "bestMenu"]; // 구조 상 타입 분기 대비(현재 동일)
  const sideList = [
    t("newbieDetail.daily") + t("newbieDetail.back"),
    t("newbieDetail.weekly") + t("newbieDetail.back"),
    t("newbieDetail.monthly") + t("newbieDetail.back"),
  ];

  // 데이터 최소 제공일자: 이 날짜 이전 선택 시 안내 후 차단
  const MIN_DATE = new Date("2025-07-31");

  // 신규 데이터 훅: 스토어의 현재 값과 로딩 상태 구독
  const {
    currentDate,
    visitDetailValue,
    isLoadingState,
    storeId,
    validDateList,
  } = useNewbieData();

  const { state } = location;

  // (디버깅 샘플) 현재 미사용. visitDetailValue를 빈 배열로 오버라이드하는 테스트 객체
  const testVisitDetailValue = {
    ...visitDetailValue,
    [page]: [], // 빈 배열로 덮어쓰기
  };

  // 토큰 검증: 없으면 로그인으로 유도(만료 체크는 별도 로직에 위임 가능)
  useEffect(() => {
    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      window.alert("데이터 최신화를 위해 재로그인을 시도합니다.");
      navigate("/login");
      return;
    }
  }, [dispatch, navigate]);

  // 라우팅 state로 진입 시 page 복원 + 스크롤 상단 고정
  useEffect(() => {
    window.scroll(0, 0);
    if (location.state?.page) setPage(location.state.page);
  }, [location.state]);

  // bfcache 복원 시(사파리 등) 열린 패널을 강제 닫아 UI 일관성 확보
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        setLeftSide(false);
        setRightSide(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // CalendarContent 재마운트를 강제하기 위한 key(페이지/날짜 조합)
  const calendarKey = `${page}-${String(currentDate ?? "")}`;

  // 모달 오픈/클로즈
  const openModalFor = (user) => {
    setSelectedPhone(user.phone_number);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setSelectedPhone("");
    setIsModalOpen(false);
  };

  // 좌/우 패널 토글(동시 열림 방지)
  const openLeftSide = () => {
    if (rightSide) setRightSide(false);
    setLeftSide(!leftSide);
  };
  const openRightSide = () => {
    if (leftSide) setLeftSide(false);
    setRightSide(!rightSide);
  };

  // 언어 변경: en↔ko 토글 후 좌측 패널 닫기
  const changeLanguage = (type) => {
    if (i18n.language === "en" && type === "ko") {
      i18n.changeLanguage("ko");
    } else if (i18n.language === "ko" && type === "en") {
      i18n.changeLanguage("en");
    }
    setLeftSide(false);
  };

  // 드롭다운에서 기간 전환
  const handleDropDown = (index) => {
    setPageType(pageTypeList[index]);
    setPage(pageList[index % 3]);
  };

  // 로딩 화면
  if (isLoadingState) {
    return (
      <LoadingScreen
        txt={
          <span>
            신규 유저 데이터를 <br /> 분석하고 있어요!
          </span>
        }
        subtxt={
          <span style={{ fontSize: "2.5vw", color: "grey" }}>
            최대 30초까지 걸릴 수 있어요!
          </span>
        }
      ></LoadingScreen>
    );
  }

  return (
    <div className="block pt-5 overflow-x-hidden">
      {/* 상단 헤더: 좌/우 패널 토글, 현재 page/날짜 전달 */}
      <Header
        leftSide={openLeftSide}
        t={t}
        i18n={i18n}
        rightSide={openRightSide}
        page={page}
        currentDate={currentDate}
      ></Header>

      {/* 좌측 드롭다운 패널 */}
      <motion.div
        className="fixed left-5 z-[9999]"
        initial={{ x: "-100%", opacity: 0 }}
        animate={{ x: leftSide ? 0 : "-100%", opacity: leftSide ? 1 : 0 }}
        exit={{ x: "-100%", opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* 열렸을 때만 마운트 → 이벤트/포커스 최소화 */}
        {leftSide && (
          <DropDownMenu
            changeLanguage={changeLanguage}
            sideList={sideList}
            open={openLeftSide}
            onclickFunction={handleDropDown}
            t={t}
            i18n={i18n}
          ></DropDownMenu>
        )}
      </motion.div>

      {/* 우측 달력 패널: 날짜 선택 시 fetchNewbie 디스패치 */}
      <motion.div
        className="fixed right-5 z-[9999]"
        initial={{ x: "+100%", opacity: 0 }}
        animate={{ x: rightSide ? 0 : "+100%", opacity: rightSide ? 1 : 0 }}
        exit={{ x: "+100%", opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {rightSide && (
          <CalendarContent
            key={calendarKey} // page/date 변경 시 재마운트 유도
            validDateList={validDateList}
            detail={pageType}
            page={page}
            currentDate={currentDate}
            open={openRightSide}
            t={t}
            i18n={i18n}
            onSelectDate={(dateString) => {
              // 최소 제공일자 검증(이전이면 경고 후 무시)
              const selected = new Date(dateString);
              if (selected < MIN_DATE) {
                window.alert(
                  t(
                    "2025년 7월 31일 이전의 데이터는 제공되지 않습니다.\n" +
                      "이후 날짜를 선택해주세요."
                  )
                );
                return;
              }

              // 선택 날짜 기준으로 신규 가입자 데이터 재조회
              dispatch(fetchNewbie(dateString));
              openRightSide(); // 선택 후 패널 닫기
            }}
          />
        )}
      </motion.div>

      {/* 신규 가입자 상세 테이블 */}
      <NewbieDetail
        storeId={storeId}
        visitDetailValue={visitDetailValue}
        page={page}
        //goBack={() => navigate(`/${page}`)}
        goBack={() => navigate(-1)} // 브라우저 히스토리 뒤로가기
        onView={openModalFor} // 행의 "조회" 클릭 → 포인트 상세 모달
        t={t}
        i18n={i18n}
      />

      {/* 포인트 상세 모달: 선택 사용자 전화번호로 조회 */}
      <ReactModal
        ariaHideApp={false}
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        style={{
          content: {
            width: "85vw",
            height: "75vh",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          },
          overlay: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <div className="modal_content">
          <PointDetails storeId={storeId} phoneNum={selectedPhone} />

          {/* 닫기 버튼(중복 존재) */}
          <button
            onClick={closeModal}
            style={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "4vw",
              padding: "0.5em 1em",
            }}
          >
            닫기
          </button>

          <div className="modal_footer">
            <button onClick={closeModal} style={{ fontSize: "4vw" }}>
              닫기
            </button>
          </div>
        </div>
      </ReactModal>

      {/* 하단 내비: 현재 키는 userRank로 전달(신규 전용 키로 정리 가능) */}
      <BottomNavbar
        changeDetail={setPage}
        page={page}
        pageType="userRank"
        info="userRank"
        currentDate={currentDate}
        t={t}
        i18n={i18n}
      />
    </div>
  );
}
